import dayjs from 'dayjs';
import { getFullName } from '@frontend/name';
import { GridAppointmentCalendarEvent, GridExceptionCalendarEvent } from '../components/calendar-view/types';
import { getTimeDiff } from '../utils/date-helpers';
import { useGetCalendarV2QueriesData } from './use-get-calendar-v2-queries-data';

// ─── Exception event builder ──────────────────────────────────────────────────

type BuildExceptionGridEventParams = {
  id: string;
  title: string;
  startHour: string; // 'HH:mm'
  endHour: string; // 'HH:mm'
  startDate: string;
  calendarDate: string;
  locationId: string;
  getScopeName: (id: string) => string;
  entityId: string;
  entityName: string;
};

/**
 * Builds a GridExceptionCalendarEvent (break or exception block) from the fields that
 * differ per call site. All four sites (office break, office exception, provider break,
 * provider exception) share the same shape — only `id` and `title` change.
 */
export function buildExceptionGridEvent({
  id,
  title,
  startHour,
  endHour,
  startDate,
  calendarDate,
  locationId,
  getScopeName,
  entityId,
  entityName,
}: BuildExceptionGridEventParams): GridExceptionCalendarEvent {
  return {
    id,
    eventType: 'exception',
    startTime: startHour,
    duration: getTimeDiff(startHour, endHour, { format: 'HH:mm' }),
    // Enriched fields for GridExceptionCard
    title,
    startDate,
    startHour,
    endHour,
    locationId,
    locationName: getScopeName(locationId ?? ''),
    entityId,
    entityName,
    calendarDate,
  } as GridExceptionCalendarEvent;
}

// ─── Break-items loop builder ─────────────────────────────────────────────────

type BreakItem = { startTime?: string; endTime?: string };

type BuildBreakEventsParams = {
  breaks: BreakItem[];
  /** Used as part of the generated event ID — calendarDateDay for office, dayOfWeek for provider */
  dayKey: string;
  startDate: string;
  calendarDate: string;
  locationId: string;
  getScopeName: (id: string) => string;
  entityId: string;
  entityName: string;
};

type BreakEventsResult = { events: GridExceptionCalendarEvent[]; ids: string[] };

/**
 * Iterates a break-items array and returns the list of GridExceptionCalendarEvents plus their IDs.
 * Eliminates the two near-identical nested forEach loops (office breaks and provider breaks).
 *
 * The caller spreads `events` into the main events array and `ids` into columnEventIds:
 *   const { events: breakEvents, ids: breakIds } = buildBreakEvents({ ... });
 *   events.push(...breakEvents);
 *   columnEventIds.push(...breakIds);
 */
export function buildBreakEvents({
  breaks,
  dayKey,
  startDate,
  calendarDate,
  locationId,
  getScopeName,
  entityId,
  entityName,
}: BuildBreakEventsParams): BreakEventsResult {
  const events: GridExceptionCalendarEvent[] = [];
  const ids: string[] = [];

  breaks.forEach((breakItem) => {
    if (!breakItem.startTime || !breakItem.endTime) return;

    const id = `break-${dayKey}-${breakItem.startTime}-${breakItem.endTime}`;
    events.push(
      buildExceptionGridEvent({
        id,
        title: 'Break',
        startHour: breakItem.startTime,
        endHour: breakItem.endTime,
        startDate,
        calendarDate,
        locationId,
        getScopeName,
        entityId,
        entityName,
      })
    );
    ids.push(id);
  });

  return { events, ids };
}

// ─── V2 appointment event builder ─────────────────────────────────────────────

type V2Appointment = ReturnType<typeof useGetCalendarV2QueriesData>['appointments'][number];

/**
 * Converts a V2 SchemaAppointment into the GridAppointmentCalendarEvent shape expected by the
 * calendar grid widget.
 *
 * V2-specific notes (documented here so the intent is clear at the call site):
 *   - startHour/endHour are derived from the ISO `appt.start` timestamp, not from HH:mm:ss strings.
 *   - eventTypeColor, backgroundColor, overlappingIndicatorVariant are always undefined — V2 has no
 *     displayOptions; defaults are applied in GridAppointmentCard.
 *   - isYesterdayEvent is always false — V2 does not support multi-day events.
 *
 * TODO: ref old map function: libs/features/schedule/src/hooks/use-get-appointments-events.ts
 */
export function buildV2AppointmentGridEvent(
  appt: V2Appointment,
  getScopeName: (id: string) => string,
  entityId: string,
  entityName: string,
  calendarDate: string
): GridAppointmentCalendarEvent {
  const startHour = dayjs(appt.start).format('HH:mm');
  const endHour = appt.duration ? dayjs(appt.start).add(appt.duration, 'minute').format('HH:mm') : startHour;

  return {
    id: appt.id ?? '',
    startTime: startHour,
    duration: appt.duration ?? 0,
    /* we consider the cancelled appointments as appointments in v2 because
     * they don't have different styling or are skipped from overlapping events in v2*/
    eventType: 'appointment',
    // Enriched fields for GridAppointmentCard
    title: appt.person ? getFullName(appt.person) : 'Unknown',
    appointmentTypeName: appt.type,
    startDate: dayjs(appt.start).format('MM/DD/YYYY'),
    startHour,
    endHour,
    locationId: appt.locationId,
    locationName: getScopeName(appt.locationId ?? ''),
    entityId,
    entityName,
    personStatus: appt.person?.status,
    calendarDate,
    // V2 has no displayOptions — defaults apply in GridAppointmentCard
    eventTypeColor: undefined,
    backgroundColor: undefined,
    overlappingIndicatorVariant: undefined,
    isYesterdayEvent: false,
  } as GridAppointmentCalendarEvent;
}
